import type { PlatoUserMe, SuperAdminMe } from '@/server/types/infer-trpc-types';
import { setLoginAsUserIdForSuperAdmin } from '@/utils/globals';
import { trpcClient } from '@/utils/trpc-client';
import type { Typestate } from 'xstate';
import { assign, createMachine } from 'xstate';
import { LOGGED_IN_AS_COOKIE } from '../constants';
import { UserType } from '../types';

export interface MyMachineContext {
    appIsLoading: boolean;
    userIsLoading: boolean;
    primaryUserType: UserType | null;
    platoUser: PlatoUserMe | null;
    tenantId: string | null;
    superAdmin: SuperAdminMe | null;
    superAdminImpersonatingPlatoUserId: string | null;
}

export interface AppTypestate extends Typestate<MyMachineContext> {
    states: {
        NOT_AUTHENTICATED: object;
        LOADED_CLERK: object;
        LOADING_PLATO_USER: object;
        LOADING_SUPER_ADMIN: object;
        CHECK_SUPER_ADMIN_WAS_PREVIOUSLY_IMPERSONATING: object;
        PLATO_USER: object;
        SUPER_ADMIN: object;
        LOADING_IMPOERSONATION_SESSION: object;
        IMPOERSONATION_SESSION: object;
        ENDING_IMPERSONATION_SESSION: object;
        LOGGING_OUT_OF_CLERK: object;
        NO_ACCOUNT: object;
    };
}

export type AppEvent =
    | { type: 'LOGOUT' }
    | { type: 'DID_LOGOUT_OF_CLERK' }
    | { type: 'USER_LOGGED_IN' }
    | { type: 'START_IMPERSONATION_SESSION'; superAdminImpersonatingPlatoUserId: string }
    | { type: 'END_IMPERSONATING_SESSION' };

const appStateMachine = () =>
    createMachine<MyMachineContext, AppEvent, AppTypestate>(
        {
            id: 'auth',
            initial: 'NOT_AUTHENTICATED',
            context: {
                appIsLoading: true,
                userIsLoading: true,
                primaryUserType: null,
                platoUser: null,
                tenantId: null,
                superAdmin: null,
                superAdminImpersonatingPlatoUserId: null,
            },
            on: {
                LOGOUT: {
                    id: 'logout',
                    target: 'LOGGING_OUT_OF_CLERK',
                },
                DID_LOGOUT_OF_CLERK: {
                    id: 'didLogoutOfClerk',
                    target: 'NOT_AUTHENTICATED',
                    actions: [
                        assign({
                            appIsLoading: true,
                            userIsLoading: true,
                            primaryUserType: null,
                            platoUser: null,
                            tenantId: null,
                            superAdmin: null,
                            superAdminImpersonatingPlatoUserId: null,
                        }),
                    ],
                },
            },
            states: {
                LOGGING_OUT_OF_CLERK: {
                    invoke: {
                        id: 'logoutOfClerk',
                        src: 'logoutOfClerk',
                    },
                },
                NOT_AUTHENTICATED: {
                    on: {
                        USER_LOGGED_IN: {
                            target: 'LOADED_CLERK',
                            actions: assign({
                                appIsLoading: false,
                            }),
                        },
                    },
                },
                LOADED_CLERK: {
                    invoke: {
                        id: 'getUserType',
                        src: 'getUserType',
                        onDone: [
                            {
                                target: 'LOADING_PLATO_USER',
                                cond: (_, event) => event.data === UserType.PlatoUser,
                                actions: assign({
                                    primaryUserType: (_, event) => event.data,
                                }),
                            },
                            {
                                target: 'LOADING_SUPER_ADMIN',
                                cond: (_, event) => event.data === UserType.SuperAdmin,
                                actions: assign({
                                    primaryUserType: (_, event) => event.data,
                                }),
                            },
                            {
                                target: 'NO_ACCOUNT',
                                actions: assign({
                                    primaryUserType: (_, __) => null,
                                }),
                            },
                        ],
                    },
                },
                LOADING_PLATO_USER: {
                    invoke: {
                        id: 'getPlatoUser',
                        src: (_, __) => {
                            return trpcClient.platoUser.me.getMe.query();
                        },
                        onDone: {
                            target: 'PLATO_USER',
                            actions: assign({
                                platoUser: (_, event) => event.data,
                                tenantId: (_, event) => event.data.platoCustomer.id,
                            }),
                        },
                    },
                },
                LOADING_SUPER_ADMIN: {
                    invoke: {
                        id: 'getSuperAdmin',
                        src: (_, __) => {
                            return trpcClient.superAdmin.me.getMe.query();
                        },
                        onDone: {
                            target: 'CHECK_SUPER_ADMIN_WAS_PREVIOUSLY_IMPERSONATING',
                            actions: assign({
                                superAdmin: (_, event) => event.data,
                            }),
                        },
                    },
                },
                CHECK_SUPER_ADMIN_WAS_PREVIOUSLY_IMPERSONATING: {
                    invoke: {
                        id: 'checkSuperAdminWasPreviouslyImpersonating',
                        src: (_, __) => {
                            return new Promise((resolve, reject) => {
                                const retrievedObject = localStorage.getItem(LOGGED_IN_AS_COOKIE) as string;

                                if (
                                    retrievedObject &&
                                    retrievedObject !== 'null' &&
                                    retrievedObject !== 'undefined'
                                ) {
                                    resolve(retrievedObject);
                                } else {
                                    resolve(null);
                                }
                            });
                        },
                        onDone: [
                            {
                                target: 'LOADING_IMPOERSONATION_SESSION',
                                cond: (_, event) =>
                                    event.data !== null &&
                                    event.data !== 'null' &&
                                    event.data !== 'undefined',
                                actions: assign({
                                    superAdminImpersonatingPlatoUserId: (_, event) => event.data,
                                }),
                            },
                            {
                                target: 'SUPER_ADMIN',
                                cond: (_, event) =>
                                    event.data === null ||
                                    event.data === 'null' ||
                                    event.data === 'undefined',
                            },
                        ],
                    },
                },
                PLATO_USER: {},
                SUPER_ADMIN: {
                    on: {
                        START_IMPERSONATION_SESSION: {
                            target: 'LOADING_IMPOERSONATION_SESSION',
                            actions: assign({
                                superAdminImpersonatingPlatoUserId: (_, event) =>
                                    event.superAdminImpersonatingPlatoUserId,
                            }),
                        },
                    },
                },
                LOADING_IMPOERSONATION_SESSION: {
                    invoke: {
                        id: 'fetchImpersonationData',
                        src: (context) => {
                            return new Promise((resolve, reject) => {
                                if (context.superAdminImpersonatingPlatoUserId) {
                                    localStorage.setItem(
                                        LOGGED_IN_AS_COOKIE,
                                        context.superAdminImpersonatingPlatoUserId,
                                    );
                                }

                                setLoginAsUserIdForSuperAdmin(
                                    context.superAdminImpersonatingPlatoUserId || null,
                                );

                                trpcClient.platoUser.me.getMe
                                    .query()
                                    .then((data) => {
                                        resolve(data);
                                    })
                                    .catch((error) => {
                                        reject(error);
                                    });
                            });
                        },
                        onDone: {
                            target: 'IMPOERSONATION_SESSION',
                            actions: [
                                assign({
                                    platoUser: (_, event) => event.data,
                                    tenantId: (_, event) => event.data.platoCustomer.id,
                                }),
                            ],
                        },
                        onError: {
                            target: 'SUPER_ADMIN',
                            actions: [
                                assign({
                                    superAdminImpersonatingPlatoUserId: (_, __) => null,
                                }),
                                () => setLoginAsUserIdForSuperAdmin(null),
                            ],
                        },
                    },
                },
                IMPOERSONATION_SESSION: {
                    on: {
                        END_IMPERSONATING_SESSION: {
                            target: 'ENDING_IMPERSONATION_SESSION', // Transition to NEW_STATE
                        },
                    },
                },
                ENDING_IMPERSONATION_SESSION: {
                    invoke: {
                        id: 'endImpersonationSession',
                        src: (_) => {
                            return new Promise((resolve, _) => {
                                localStorage.removeItem(LOGGED_IN_AS_COOKIE);
                                resolve(true);
                            });
                        },
                        onDone: {
                            target: 'SUPER_ADMIN',
                            actions: [
                                assign({
                                    superAdminImpersonatingPlatoUserId: null,
                                    platoUser: null,
                                }),
                                () => setLoginAsUserIdForSuperAdmin(null),
                            ],
                        },
                    },
                },
                NO_ACCOUNT: {},
            },
        },
        {
            services: {
                getUserType: async (_, __) => {
                    return trpcClient.dangerousGenericUser.userTypeCheck.check.query();
                },
                getPlatoUser: async (_, __) => {
                    return trpcClient.platoUser.me.getMe.query();
                },
            },
        },
    );

export { appStateMachine };
